import React, { Fragment, useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useSnapCarousel } from 'react-snap-carousel';
import { useUnmount } from 'react-use';
import { Box, useTheme } from '@mui/material';
import { trackEvent } from '@surfline/web-common';

import { useMaxWidthTablet } from 'hooks/useMediaQueries';
import { oneSecond } from 'utils/time';

import useClientSize from 'hooks/useClientSize';
import Crowd from '../Crowd';
import WaveDistribution from '../WaveDistribution';
import getDayName from '../../utils/getDayName';

import styles from './CameraInsightsCarousel.module.scss';

const CAROUSEL_INITIAL_PAGES_SSR = [[0, 1]];

export type CameraInsightsSlides = 'summary' | 'waveDistribution' | 'crowd';

const trackFinalChartViewed = (
  pageIndex: number,
  isMobile: boolean,
  scrollEventAnalyticsValues: Record<string, any>,
) => {
  if (isMobile && pageIndex === 1)
    return trackEvent('Camera Insights Scrolled', {
      finalChartViewed: 'Crowd',
      ...scrollEventAnalyticsValues,
    });

  return null;
};

export interface CameraInsightsCarouselProps {
  crowd: any;
  endDate?: number;
  insights: any;
  scrollEventAnalyticsValues: Record<any, any>;
  isTooDark?: boolean;
  isBeforeToday?: boolean;
  timezone: string;
}

const CameraInsightsCarousel: React.FC<CameraInsightsCarouselProps> = ({
  crowd,
  endDate,
  insights,
  scrollEventAnalyticsValues,
  isTooDark = false,
  isBeforeToday = false,
  timezone,
}) => {
  const theme = useTheme();
  const isMobile = useMaxWidthTablet();
  const [finalSlideViewedIndex, setFinalSlideViewedIndex] = useState(0);

  const containerRef = useRef<HTMLElement>(null);
  const { isResizing } = useClientSize(containerRef.current, {
    observeHeight: false,
    observeWidth: true,
  });
  const { scrollRef, pages, activePageIndex, goTo, snapPointIndexes, refresh } = useSnapCarousel({
    initialPages: CAROUSEL_INITIAL_PAGES_SSR,
  });

  const nowDate = useMemo(() => new Date(), []);

  const { data: insightsData, error: insightsError, loading: insightsLoading } = insights;
  const { data: crowdData, error: crowdError, loading: crowdLoading } = crowd;

  const contentKey = useMemo(() => JSON.stringify(pages), [pages]);

  const goToPage = useCallback((pageIndex: number) => () => goTo(pageIndex), [goTo]);

  const slides = useMemo(() => {
    const mobileSlides = [['waveDistribution'], ['crowd']];
    const singleSlide = [['waveDistribution', 'crowd']];
    if (isMobile) return mobileSlides;
    return singleSlide;
  }, [isMobile]) as Array<Array<CameraInsightsSlides>>;

  const lastFullDayName = useMemo(() => {
    if (isTooDark && insightsData?.lastFullDay?.endTimestamp)
      return getDayName(
        new Date(insightsData.lastFullDay.endTimestamp * oneSecond),
        nowDate,
        timezone,
      );
    if (endDate) return getDayName(new Date(endDate), nowDate, timezone);
    return undefined;
  }, [endDate, insightsData, isTooDark, nowDate, timezone]);

  const crowdProps = useMemo(
    () => ({
      compareMessage:
        (isBeforeToday && lastFullDayName) || isTooDark ? `${lastFullDayName}'s summary` : null,
      data: { predicted: crowdData?.days?.[0], observed: crowdData?.observed },
      error: crowdError ? 'Something went wrong here.' : null,
      isLive: !isTooDark,
      isLoading: crowdLoading,
      isResizing,
    }),
    [crowdData, crowdError, crowdLoading, isBeforeToday, isResizing, isTooDark, lastFullDayName],
  );

  const waveDistributionProps = useMemo(() => {
    const tooDarkCompareMessage =
      isTooDark && lastFullDayName ? `${lastFullDayName}'s summary` : '';

    if (isTooDark) {
      return {
        compareMessage: tooDarkCompareMessage,
        data: insightsData?.lastFullDay?.waveDistribution,
        error:
          !!insightsError ||
          insightsData?.lastFullDay?.status?.waveDistribution?.available === false
            ? {
                statusCode: 500,
                message: insightsData?.lastFullDay?.status?.waveDistribution?.errorMessage,
              }
            : false,
        isLive: false,
        isLoading: insightsLoading,
        isResizing,
        units: insightsData?.associated?.units,
      };
    }

    return {
      compareMessage: insightsData?.compare?.waveDistribution,
      data: insightsData?.current?.waveDistribution,
      error:
        !!insightsError || insightsData?.current?.status?.waveDistribution?.available === false
          ? {
              statusCode: 500,
              message: insightsData?.current?.status?.waveDistribution?.errorMessage,
            }
          : false,
      isLive: true,
      isLoading: insightsLoading,
      isResizing,
      units: insightsData?.associated?.units,
    };
  }, [insightsData, insightsError, insightsLoading, isResizing, isTooDark, lastFullDayName]);

  const scrollableContainerInnerStyles = useCallback(
    (slideIndex: number) => ({
      scrollSnapAlign: snapPointIndexes?.has(slideIndex) ? 'start' : 'none',
    }),
    [snapPointIndexes],
  );

  const slideElements = useMemo(
    () =>
      slides.map((slide, slideIndex) => (
        <Box
          className={styles.scrollableContainerInner}
          key={`${slide?.[slideIndex]}${slideIndex + 1}`}
          style={scrollableContainerInnerStyles(slideIndex)}
        >
          {slide?.map((mod, i) => (
            <Fragment key={`${mod}-${i + 1}`}>
              {mod === 'waveDistribution' && <WaveDistribution {...waveDistributionProps} />}
              {mod === 'crowd' && <Crowd {...crowdProps} />}
            </Fragment>
          ))}
        </Box>
      )),
    [crowdProps, scrollableContainerInnerStyles, slides, waveDistributionProps],
  );

  const pageControlsStyles = useCallback(
    (pageIndex: number) => ({
      background:
        pageIndex === activePageIndex ? theme.palette.text.base : theme.palette.text.secondary,
    }),
    [activePageIndex, theme.palette.text.base, theme.palette.text.secondary],
  );

  const pageButtons = useMemo(
    () =>
      slides?.length > 1 ? (
        <div className={styles.controlsWrapper}>
          {pages.map((_, pageIndex) => (
            <button
              aria-label="mobile Smart cam insights controls"
              className={styles.carouselControls}
              key={`button${pageIndex + 1}`}
              onClick={goToPage(pageIndex)}
              style={pageControlsStyles(pageIndex)}
              type="button"
            />
          ))}
        </div>
      ) : null,
    [goToPage, pageControlsStyles, pages, slides?.length],
  );

  useEffect(() => {
    if (activePageIndex > finalSlideViewedIndex) setFinalSlideViewedIndex(activePageIndex);
  }, [activePageIndex, finalSlideViewedIndex, setFinalSlideViewedIndex]);

  useEffect(() => {
    refresh();
  }, [slides, refresh]);

  useUnmount(() =>
    trackFinalChartViewed(finalSlideViewedIndex, isMobile, scrollEventAnalyticsValues),
  );

  return (
    <Fragment key={contentKey}>
      <Box className={styles.contentInner} ref={containerRef}>
        <Box className={styles.scrollContainer} ref={scrollRef}>
          {slideElements}
        </Box>
      </Box>
      {pageButtons}
    </Fragment>
  );
};

export default CameraInsightsCarousel;
